<script setup>
import { markRaw, ref, getCurrentInstance, defineProps } from 'vue';
import { Canvas, OrbitCameraComponent } from 'atlatl-visual-runtime';
import { RefreshIcon, ArrowBigRightIcon } from 'vue-tabler-icons';

const props = defineProps({
  canvas: {
    type: Canvas,
    required: true,
  },
});
const instance = getCurrentInstance();

const camera = ref(markRaw(props.canvas.getComponentByType(OrbitCameraComponent)));

const refresh = () => instance.proxy.$forceUpdate();

const set = (toOption, fromValue) => {
  const setValue = props.canvas.getComponentByType(OrbitCameraComponent)[fromValue];
  camera.value[toOption] = setValue;
  instance.proxy.$forceUpdate();
};
</script>

<template>
  <div class="flex h-full flex-col gap-2 bg-white p-2 text-sm">
    <button class="camera--sync-button" @click="refresh"><refresh-icon></refresh-icon> Refresh</button>
    <div class="flex flex-grow flex-col gap-2">
      <div class="camera-field">
        Radius
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <button class="camera--sync-button camera--radius-min" @click="set('lowerRadiusLimit', 'radius')">
            <arrow-big-right-icon class="h-4 w-4" />
          </button>
          <input id="radius-min" v-model.number="camera.lowerRadiusLimit" type="number" step="0.01" min="0" />
          <label>Min</label>
        </div>
        <div class="flex gap-2 rounded bg-white px-2 py-1">
          <input
            id="radius-value"
            v-model.number="camera.radius"
            type="number"
            step="0.01"
            :min="camera.lowerRadiusLimit"
            :max="camera.upperRadiusLimit"
          />
        </div>
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <button class="camera--sync-button camera--radius-max" @click="set('upperRadiusLimit', 'radius')">
            <arrow-big-right-icon class="h-4 w-4" />
          </button>
          <input id="radius-max" v-model.number="camera.upperRadiusLimit" type="number" step="0.01" min="0" />
          <label>Max</label>
        </div>
      </div>
      <div class="camera-field">
        Alpha
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <button class="camera--sync-button camera--alpha-min" @click="set('lowerAlphaLimit', 'alpha')">
            <arrow-big-right-icon class="h-4 w-4" />
          </button>
          <input id="alpha-min" v-model.number="camera.lowerAlphaLimit" type="number" step="0.01" min="0" />
          <label>Min</label>
        </div>
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <input
            id="alpha-value"
            v-model.number="camera.alpha"
            type="number"
            step="0.01"
            :min="camera.lowerAlphaLimit"
            :max="camera.upperAlphaLimit"
          />
        </div>
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <button class="camera--sync-button camera--alpha-max" @click="set('upperAlphaLimit', 'alpha')">
            <arrow-big-right-icon class="h-4 w-4" />
          </button>
          <input id="alpha-max" v-model.number="camera.upperAlphaLimit" type="number" step="0.01" min="0" />
          <label>Max</label>
        </div>
      </div>
      <div class="camera-field">
        Beta
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <button class="camera--sync-button camera--beta-min" @click="set('lowerBetaLimit', 'beta')">
            <arrow-big-right-icon class="h-4 w-4" />
          </button>
          <input id="beta-min" v-model.number="camera.lowerBetaLimit" type="number" step="0.01" min="0" />
          <label>Min</label>
        </div>
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <input
            id="beta-value"
            v-model.number="camera.beta"
            type="number"
            step="0.01"
            :min="camera.lowerBetaLimit"
            :max="camera.upperBetaLimit"
          />
        </div>
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <button class="camera--sync-button camera--beta-max" @click="set('upperBetaLimit', 'beta')">
            <arrow-big-right-icon class="h-4 w-4" />
          </button>
          <input id="beta-max" v-model.number="camera.upperBetaLimit" type="number" step="0.01" min="0" />
          <label>Max</label>
        </div>
      </div>
      <div class="camera-field">
        Target
        <div class="grid grid-cols-3 gap-2">
          <div class="flex flex-col">
            <label class="flex px-2">x</label>
            <input
              v-model.number="camera.target[0]"
              type="number"
              step="0.01"
              min="0"
              class="outline-none w-full flex-grow rounded-xl p-2"
            />
          </div>
          <div class="flex flex-col">
            <label class="flex px-2">y</label>
            <input
              v-model.number="camera.target[1]"
              type="number"
              step="0.01"
              min="0"
              class="outline-none w-full flex-grow rounded-xl p-2"
            />
          </div>
          <div class="flex flex-col">
            <label class="flex px-2">z</label>
            <input
              v-model.number="camera.target[2]"
              type="number"
              step="0.01"
              min="0"
              class="outline-none w-full flex-grow rounded-xl p-2"
            />
          </div>
        </div>
      </div>
      <div class="camera-field">
        Far Clip Distance
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <input v-model.number="camera.farClipDistance" type="number" step="0.01" min="0" />
        </div>
      </div>
      <div class="camera-field">
        Near Clip Distance
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <input v-model.number="camera.nearClipDistance" type="number" step="0.01" min="0" />
        </div>
      </div>
      <div class="camera-field">
        <div class="flex items-baseline">
          <label for="" class="flex-grow"> Pan Enabled </label>
          <input v-model="camera.panEnabled" type="checkbox" />
        </div>
      </div>
      <div class="camera-field">
        <div class="flex items-baseline">
          <label for="" class="flex-grow"> Limit Radius Using Bounds </label>
          <input v-model="camera.limitRadiusUsingBounds" type="checkbox" />
        </div>
      </div>
      <div class="camera-field">
        Panning Sensitivity
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <input v-model.number="camera.panningSensitivity" type="number" step="0.01" min="0" />
        </div>
      </div>
      <div class="camera-field">
        Pinch Precision
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <input v-model.number="camera.pinchPrecision" type="number" step="0.01" min="0" />
        </div>
      </div>
      <div class="camera-field">
        Speed
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <input v-model.number="camera.speed" type="number" step="0.01" min="0" />
        </div>
      </div>
      <div class="camera-field">
        Wheel Precision
        <div class="flex items-center gap-2 rounded bg-white px-2 py-1">
          <input v-model.number="camera.wheelPrecision" type="number" step="0.01" min="0" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.camera-field {
  @apply flex flex-col gap-2 rounded bg-offwhite px-3 py-2;
}

.camera-field input[type='number'] {
  @apply outline-none w-full flex-grow;
}

.camera--sync-button {
  @apply mx-auto flex items-center gap-2 rounded bg-brand-500 px-2 py-1 text-white;
}
</style>
